<template>
  <div
    v-show="showOverlay"
    class="shop-the-look-popover"
  >
    <overlay
      :is-active="showOverlay"
      parent-class="shop-the-look-popover"
      @close-overlay="closeOverlay"
      @before-unmount="onBeforeUnmount"
    >
      <template #body>
        <div
          ref="shopTheLookPopover"
          class="shop-the-look-popover__wrapper"
          @focusout="handleFocusOut"
        >
          <header
            class="shop-the-look-popover__header overlay__header"
            js-overlay="headerElement"
          >
            <h2
              v-if="overlayTitle && isMobile"
              class="shop-the-look-popover__heading"
              v-text="overlayTitle"
            />
            <button
              class="shop-the-look-popover__close overlay__close"
              :aria-label="closeOverlayText"
              type="button"
              @click.prevent="closeOverlay"
            >
              <icon-close />
            </button>
          </header>

          <div
            class="overlay__content shop-the-look-popover__content"
            :class="{ 'full-width': !image || isMobile }"
            js-overlay="scrollElement"
          >
            <figure
              v-if="image && !isMobile"
              ref="mediaElement"
              class="shop-the-look-popover__media"
            >
              <responsive-image
                min-max="400-800"
                :image="image"
                :alt="image.alt"
                @set-aspect-ratio="setAspectRatio"
              />
            </figure>
            <div
              v-if="renderedProducts?.length"
              class="shop-the-look-popover__carousel"
            >
              <h2
                v-if="overlayTitle && !isMobile"
                class="shop-the-look-popover__heading"
                v-text="overlayTitle"
              />
              <product-carousel
                context="shop-the-look-popover"
                class="shop-the-look-popover__products"
                :products="renderedProducts"
                :section-id="liquid.sectionId"
                :tracking-parameters="trackingParameters"
                :product-card-use-packshot="true"
                :slides-per-view="slidesPerView"
                :prevent-drawer="true"
                :hide-wishlist-icon="true"
                :hide-product-swatches="true"
              />
            </div>
          </div>
        </div>
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Component: Shop The Look Popover
 * -----------------------------------------------------------------------------
 * - Modal that displays product cards carousel.
 *
 * @param {String} [liquid.sectionId] - Section's Liquid ID.
 */
import { mapState } from 'pinia'

import { useScreenStore } from '~stores/screen'

import { nextTick } from 'vue'

import classes from '~/helpers/classes'
import { importComponent } from '~/core/mint/helpers'
import { getFocusableElements } from '~/helpers/utils'

import ResponsiveImage from '~components/responsive-image/responsive-image'
import ProductCarousel from '~components/product-carousel/product-carousel'
import Overlay from '~components/overlay/overlay'

import IconClose from '~icons/misc/close.svg'

export default {
  name: 'ShopTheLookPopover',

  components: {
    ResponsiveImage,
    ProductCarousel,
    IconClose,
    Overlay,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
  },

  data() {
    return {
      activeNamespace: '',
      cachedData: {},
      image: false,
      lastActiveElement: null,
      productHandles: [],
      renderedProducts: [
        {
          key: 0,
        },
        {
          key: 1,
        },
        {
          key: 2,
        },
        {
          key: 3,
        },
      ],
      showOverlay: false,
      trackingParameters: {},
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Calculate the 'Close' button text.
     * @returns {String}
     */
    closeOverlayText() {
      return mint.strings.accessibility.close
    },

    /**
     * Calculate the 'Shop the look' title text.
     * @returns {String}
     */
    overlayTitle() {
      return mint.Strings('shop_the_look.title')
    },

    /**
     * Calculate the 'Default Marketing Attribution' value.
     * @returns {String}
     */
    defaultMarketingAttribution() {
      return mint.shopTheLookPopover?.defaultMarketingAttribution
    },

    /**
     * Calculate the 'Slides Per View' for each breakpoint.
     * @returns {Object}
     */
    slidesPerView() {
      return {
        mobile: 1.25,
        tablet: 2.5,
        desktop: 3.15,
      }
    },
  },

  watch: {

    /**
     * Listen for if the device has been resized to mobile or desktop.
     * - Re-add 'is-locked' class to the body (if the overlay is open) in case
     * it was removed within other components.
     */
    isMobile() {
      nextTick(() => {
        if (this.showOverlay) {
          document.body.classList.add(classes.locked)
        }
      })
    },
  },

  mounted() {
    this.setEventBusListeners()
  },

  methods: {

    /**
     * Opens the overlay and manages focus.
     */
    openOverlay() {
      if (this.showOverlay) {
        return
      }

      this.showOverlay = true
      this.lastActiveElement = document.activeElement

      nextTick(() => {
        const focusableElements = getFocusableElements(
          this.$refs.shopTheLookPopover,
        )

        if (focusableElements.length) {
          focusableElements[0].focus()
        }

        document.body.classList.add(classes.locked)
      })
    },

    /**
     * Closes the overlay and restores the previous focus.
     */
    closeOverlay() {
      if (!this.showOverlay) {
        return
      }

      document.body.classList.remove(classes.locked)
      this.showOverlay = false

      /**
       * Return focus to the last active element if keyboard navigation has
       * been detected.
       */
      if (
        this.lastActiveElement &&
        mint.accessibility.keyboardNavigation
      ) {
        this.lastActiveElement.focus()
      }
    },

    /**
     * Handles focus out events from the overlay.
     * - Closes the overlay if focus moves outside of it.
     *
     * @param {FocusEvent} event - The focus event object.
     */
    handleFocusOut(event) {
      if (!mint.accessibility.keyboardNavigation) {
        return
      }

      if (!this.$refs.shopTheLookPopover.contains(event.relatedTarget)) {
        this.closeOverlay()
      }
    },

    /**
     * Set the products that will be displayed in the modal.
     */
    async setProducts() {
      if (this.cachedData[this.activeNamespace]) {
        this.renderedProducts = this.cachedData[this.activeNamespace]

        return
      }

      try {
        const queries = this.productHandles.map((handle) => {
          return mint.GetProduct({ handle })
        })

        const response = await Promise.allSettled(queries)

        /**
         * If the query fails, it's likely that the product isn't
         * available on the Storefront API channel.
         */
        this.renderedProducts = response
          .map((promise) => {
            if (promise.status !== 'fulfilled' || !promise.value?.handle) {
              return false
            }

            return promise.value
          })
          .filter(Boolean)
      } catch (error) {
        mint.Log('error', 'Error fetching Shop The Look products', error)
      } finally {

        /**
         * Duplicate the body lock here
         * - This is because the `<overlay>` removes 'is-locked' class within
         * `beforeUnmount` hook
         * - It's often triggered on product cards (presumably - because of
         * re-renders) after modal is already opened
         */
        nextTick(() => {
          this.showOverlay
            ? document.body.classList.add(classes.locked)
            : document.body.classList.remove(classes.locked)
        })
      }

      this.cachedData[this.activeNamespace] = this.renderedProducts
    },

    /**
     * Set the CSS aspect ratio
     * - Runs after child component event.
     * @param {String} aspectRatio - Valid CSS aspect ratio
     */
    setAspectRatio(aspectRatio) {
      if (!aspectRatio) {
        return
      }

      this.$refs.mediaElement.style.setProperty(
        '--aspect-ratio',
        aspectRatio,
      )
    },

    /**
     * Update Popover content and active state variables.
     *
     * - This method is used as a callback for the `shopTheLookPopover:open` event.
     * - It updates the product handles, image, and tracking data to reflect
     *   the state in the Popover.
     *
     * @param {Object} payload - Data passed from the `shopTheLookPopover:open` event.
     * @param {Element} payload.target - The element that triggered the Popover.
     * @param {String} payload.namespace - The handleized name/ID attribute of the corresponding shop-the-look section.
     * @param {Object} payload.data - The section's data, containing products and settings.
     * @param {String[]} [payload.data.productHandles] - An array of product handles.
     * @param {Object|Boolean} [payload.data.image] - The image associated with the Popover.
     * @param {Object} [payload.data.trackingParameters] - Tracking parameters for analytics.
     */
    setPopoverState({ target, namespace, data }) {
      const {
        image = false,
        productHandles = [],
        trackingParameters = {},
      } = data

      /**
       * - Check if the same popover has been called again
       * - If no - reset the products state
       * - Important - to remove handles in order to re-trigger methods
       * within the `product-carousel` (by default it assumes that products
       * data won't change)
       */
      if (this.activeNamespace != namespace) {
        this.renderedProducts = [
          { key: 0 },
          { key: 1 },
          { key: 2 },
          { key: 3 },
        ]
      }

      this.activeNamespace = namespace
      this.productHandles = productHandles
      this.image = image

      trackingParameters.section
        ? (this.trackingParameters = trackingParameters)
        : (this.trackingParameters = {
          section: this.defaultMarketingAttribution,
        })
    },

    /**
     * Set EventBus Listeners.
     */
    setEventBusListeners() {
      mint.EventBus.on(
        'shopTheLookPopover:open',
        ({ target, namespace, data }) => {
          this.setPopoverState({ target, namespace, data })
          this.openOverlay()
          this.setProducts()
        },
      )

      mint.EventBus.on('shopTheLookPopover:close', () => {
        this.closeOverlay()
      })

      mint.EventBus.on('product:openBackInStock', () => {
        this.closeOverlay()
      })
    },
  },
}
</script>

<style src="./shop-the-look-popover.scss" lang="scss"></style>
